<template>
  <v-container fluid>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="ebookingsSettings"
        :options.sync="options"
        item-key="country_code"
        :footer-props="footerProps"
        :loading-text="$t('settings.loadingSettings')"
        :no-data-text="$t('settings.noSettings')"
        mobile-breakpoint="481"
        :loading="loadingData"
        :server-items-length="totalEbookingsSettings"
        dense
      >
        <template v-slot:top>
          <v-toolbar flat class="my-1">
            <v-row v-if="$vuetify.breakpoint.width >= 520" no-gutters align="center">
              <v-col cols="9" md="7" xl="6" class="pr-2">
                <countries-autocomplete
                  v-model="selectedCountries"
                  :label="$t('settings.country')"
                  clearable
                  dense
                  hide-details
                  outlined
                  multiple
                  required
                  @blur="reloadList"
                  @click:clear="reloadList"
                ></countries-autocomplete>
              </v-col>
              <v-col cols="auto">
                <v-btn @click="createSetting" color="primary" outlined><v-icon>mdi-plus</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-toolbar v-if="$vuetify.breakpoint.width < 520" flat class="my-1">
            <countries-autocomplete
              v-model="selectedCountries"
              :label="$t('settings.country')"
              clearable
              dense
              hide-details
              outlined
              multiple
              required
              class="mr-2 flex-fill-min"
              @blur="reloadList"
              @click:clear="reloadList"
            ></countries-autocomplete>
            <v-btn @click="createSetting" color="primary" outlined><v-icon>mdi-plus</v-icon></v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.country_code="{ item }">
          {{ item.country_code }}
        </template>
        <template v-slot:item.updated_at="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn
            :class="[$vuetify.breakpoint.width >= 481 ? '' : 'pr-0']"
            text
            color="primary"
            @click="executeAction(item.country_code)"
          >
            {{ $t('return.settings.details') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    <simple-choice-dialog
      v-model="formSettingsDialog"
      :title="$t('settings.selectCountry')"
      :introductionSentence="$t('settings.selectACountryBelow')"
      :choices="countriesWithNoSettings"
      :choicesListLabel="$t('settings.country')"
      @confirm="executeAction"
    ></simple-choice-dialog>
  </v-container>
</template>

<script>
  import { EventBus } from '@/event-bus'
  import { mapState } from 'vuex'
  import moment from 'moment'
  import SimpleChoiceDialog from '../../../components/dialogs/SimpleChoiceDialog.vue'
  import CountriesAutocomplete from '../../../components/CountriesAutocomplete.vue'

  export default {
    components: { SimpleChoiceDialog, CountriesAutocomplete },
    name: 'EbookingsSettingsTable',
    data() {
      return {
        ebookingsSettings: [],
        options: {
          itemsPerPage: 15,
        },
        loadingData: true,
        totalEbookingsSettings: 0,
        filters: [],
        formSettingsDialog: false,
        settingToUpdate: null,
        countries: [],
        selectedCountries: [],
        countriesWithSettingsLoaded: false,
        countriesWithSettings: [],
      }
    },
    mounted() {
      this.countries = this.$loadCountries(this.$i18n.locale)
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'roleRights']),
      headers() {
        return [
          {
            text: this.$t('return.settings.countryCode'),
            value: 'country_code',
            filterType: 'text',
            sortable: true,
          },
          {
            text: this.$t('settings.lastUpdate'),
            value: 'updated_at',
            filterType: 'text',
            sortable: true,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ]
      },
      countriesWithNoSettings() {
        if (this.countriesWithSettings.length && this.countries.length) {
          return this.countries
            .filter((country) => !this.countriesWithSettings.includes(country.iso_3_code))
            .map((country) => {
              return {
                display: `(${country.iso_3_code})  ${country.name}`,
                value: country.iso_3_code,
              }
            })
        } else {
          return this.countries.map((country) => {
            return {
              display: `(${country.iso_3_code})  ${country.name}`,
              value: country.iso_3_code,
            }
          })
        }
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('settings.configurationsPerPage'),
        }
      },
    },
    methods: {
      filter(filters) {
        this.filters = filters
        this.reloadList()
      },
      executeAction: function (countryCode) {
        this.$router.push({ path: `/customizations/ebookings/${countryCode}` })
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        this.options.page = 1
        this.list()
      },
      list() {
        this.loadingData = true
        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            sortBy: sortBy.length === 0 ? 'date' : sortBy,
            sortDesc: sortDesc.length === 0 ? true : sortDesc,
            page: page,
            itemsPerPage: itemsPerPage,
          },
        }
        if (this.selectedCountries.length > 0) {
          myInit.queryStringParameters.countries = this.selectedCountries
        }

        this.$Amplify.API.get('ebookings', '/v1/int/ebookings/settings', myInit)
          .then((res) => {
            this.ebookingsSettings = res.settings
            this.totalEbookingsSettings = res.nbEbookingsSetting
            this.loadingData = false
            if (!this.countriesWithSettingsLoaded) {
              for (const settings of this.ebookingsSettings) {
                this.countriesWithSettings.push(settings.country_code)
              }
              this.countriesWithSettingsLoaded = true
            }
          })
          .catch((e) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e.message,
              color: 'error',
            })
            this.loadingData = false
          })
      },
      createSetting() {
        this.settingToUpdate = null
        this.formSettingsDialog = true
      },
      close() {
        this.formSettingsDialog = false
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YY HH:mm') : ''
      },
    },
  }
</script>

<style scoped>
.flex-fill-min {
  flex: 1;
  min-width: 0;
}
</style>
